// packages/webapp/src/contexts/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode, useMemo } from 'react';
import { onAuthStateChanged, User, Auth, getIdToken } from 'firebase/auth';
// Import initialize function and getters
import {
    _initializeFirebase, // Import the initializer
    getFirebaseAuth,
    signIn,
    signUp,
    signInWithGoogle,
    logOut
} from '../lib/firebase-client';

// Define the shape of our auth context
interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  logOut: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
}

// Initialize with undefined and proper type
// Export AuthContext and AuthContextType
export const AuthContext = createContext<AuthContextType | undefined>(undefined);
export type { AuthContextType }; // Export the type

// Helper to check if we're in a browser environment
const isBrowser = () => typeof window !== 'undefined';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Function to refresh the token and get the latest custom claims
  const refreshToken = async (): Promise<string | null> => {
    if (!user) return null;
    
    try {
      // Force refresh to get the most recent token with updated custom claims
      const token = await getIdToken(user, true);
      return token;
    } catch (error) {
      console.error("[AuthContext] Error refreshing token:", error);
      return null;
    }
  };

  useEffect(() => {
    // Early return for SSR
    if (!isBrowser()) {
      setLoading(false);
      return;
    }

    // Trigger Firebase Initialization
    _initializeFirebase();

    // Setup auth state listener with retry mechanism
    const setupAuthListener = () => {
      // Get the auth instance
      const auth = getFirebaseAuth();

      if (!auth) {
        console.warn("[AuthContext] Firebase auth instance not available yet, will retry...");
        // Retry after a short delay
        setTimeout(setupAuthListener, 100);
        return;
      }

      // Set up auth state listener
      const unsubscribe = onAuthStateChanged(auth,
        // Success callback
        (authUser) => {
          setUser(authUser);
          setLoading(false);
          
          // Only get the token without forcing a refresh on initial load
          // This prevents unnecessary calls to Firebase auth
        },
        // Error callback
        (error) => {
          console.error("[AuthContext] Auth state error:", error);
          setLoading(false);
        }
      );

      return unsubscribe;
    };

    // Start the setup process
    const unsubscribe = setupAuthListener();

    // Cleanup subscription on unmount
    return () => {
      if (typeof unsubscribe === 'function') {
        console.log("[AuthContext] Cleaning up auth state listener");
        unsubscribe();
      }
    };
  }, []); // Run only once on mount

  // Memoize the context value
  const value = useMemo(() => ({
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    logOut,
    refreshToken
  }), [user, loading]);

  // For SSR, render children directly without the provider
  if (!isBrowser()) {
    return <>{children}</>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
